import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ankiAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from './ui/dialog';
import { Download, Loader2, Search, CheckSquare, Square, FileText, Layers } from 'lucide-react';
import { toast } from 'sonner';

export function AnkiExportModal({ open, onOpenChange }) {
    const { isDark } = useTheme();
    const [concepts, setConcepts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [format, setFormat] = useState('basic');
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (open) {
            fetchConcepts();
        } else {
            setSelected(new Set());
            setSearch('');
        }
    }, [open]);

    const fetchConcepts = async () => {
        setLoading(true);
        try {
            const res = await ankiAPI.getConcepts();
            setConcepts(res.data.concepts || []);
        } catch {
            toast.error('Failed to load concepts');
        } finally {
            setLoading(false);
        }
    };

    const toggleConcept = (key) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const selectAll = () => {
        const keys = filtered.map(c => `${c.subject}::${c.concept}`);
        setSelected(new Set(keys));
    };

    const deselectAll = () => setSelected(new Set());

    const filtered = concepts.filter(c => {
        const q = search.toLowerCase();
        return !q || c.concept.toLowerCase().includes(q) || c.subject.toLowerCase().includes(q);
    });

    const totalCards = concepts
        .filter(c => selected.has(`${c.subject}::${c.concept}`))
        .reduce((sum, c) => sum + c.question_count, 0);

    const handleExport = async () => {
        if (selected.size === 0) {
            toast.error('Select at least one concept');
            return;
        }
        setExporting(true);
        try {
            const res = await ankiAPI.exportDeck(Array.from(selected), format);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'MedPrep_Anki_Export.apkg';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Exported ${totalCards} cards to Anki`);
            onOpenChange(false);
        } catch {
            toast.error('Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={cn(
                "sm:max-w-lg max-h-[85vh] flex flex-col",
                isDark && "bg-slate-900 border-slate-700 text-slate-100"
            )} data-testid="anki-export-modal">
                <DialogHeader>
                    <DialogTitle className={cn("flex items-center gap-2 text-lg", isDark && "text-white")}>
                        <Download className="h-5 w-5 text-indigo-500" />
                        Export to Anki
                    </DialogTitle>
                    <DialogDescription className={isDark ? "text-slate-400" : ""}>
                        Select concepts from your missed questions to create an Anki flashcard deck.
                    </DialogDescription>
                </DialogHeader>

                {/* Format selector */}
                <div className="flex gap-2" data-testid="anki-format-selector">
                    <button
                        onClick={() => setFormat('basic')}
                        className={cn(
                            "flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all",
                            format === 'basic'
                                ? isDark ? "bg-indigo-900/50 border-indigo-500 text-indigo-300" : "bg-indigo-50 border-indigo-300 text-indigo-700"
                                : isDark ? "bg-slate-800 border-slate-600 text-slate-300 hover:border-slate-500" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                        )}
                        data-testid="anki-format-basic"
                    >
                        <FileText className="h-4 w-4" />
                        Basic
                    </button>
                    <button
                        onClick={() => setFormat('cloze')}
                        className={cn(
                            "flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all",
                            format === 'cloze'
                                ? isDark ? "bg-indigo-900/50 border-indigo-500 text-indigo-300" : "bg-indigo-50 border-indigo-300 text-indigo-700"
                                : isDark ? "bg-slate-800 border-slate-600 text-slate-300 hover:border-slate-500" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                        )}
                        data-testid="anki-format-cloze"
                    >
                        <Layers className="h-4 w-4" />
                        Cloze
                    </button>
                </div>

                {/* Search + select controls */}
                <div className="flex items-center gap-2">
                    <div className={cn("flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border text-sm",
                        isDark ? "bg-slate-800 border-slate-600" : "bg-white border-slate-200"
                    )}>
                        <Search className="h-4 w-4 text-slate-400 shrink-0" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search concepts..."
                            className={cn("w-full bg-transparent outline-none text-sm", isDark ? "text-slate-200 placeholder:text-slate-500" : "text-slate-800 placeholder:text-slate-400")}
                            data-testid="anki-search-input"
                        />
                    </div>
                    <Button variant="ghost" size="sm" onClick={selectAll} className={cn("text-xs shrink-0", isDark && "text-slate-300 hover:bg-slate-800")} data-testid="anki-select-all">
                        All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={deselectAll} className={cn("text-xs shrink-0", isDark && "text-slate-300 hover:bg-slate-800")} data-testid="anki-deselect-all">
                        None
                    </Button>
                </div>

                {/* Concept list */}
                <div className={cn(
                    "flex-1 overflow-y-auto min-h-0 rounded-lg border divide-y",
                    isDark ? "border-slate-700 divide-slate-700" : "border-slate-200 divide-slate-100"
                )} style={{ maxHeight: '320px' }} data-testid="anki-concept-list">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className={cn("text-center py-12 text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                            {concepts.length === 0 ? "No missed questions yet. Practice more to generate export data." : "No concepts match your search."}
                        </div>
                    ) : (
                        filtered.map(c => {
                            const key = `${c.subject}::${c.concept}`;
                            const isSelected = selected.has(key);
                            return (
                                <button
                                    key={key}
                                    onClick={() => toggleConcept(key)}
                                    className={cn(
                                        "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors",
                                        isSelected
                                            ? isDark ? "bg-indigo-900/30" : "bg-indigo-50/70"
                                            : isDark ? "hover:bg-slate-800/50" : "hover:bg-slate-50"
                                    )}
                                    data-testid={`anki-concept-${c.concept.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                    {isSelected ? (
                                        <CheckSquare className="h-4 w-4 text-indigo-500 shrink-0" />
                                    ) : (
                                        <Square className={cn("h-4 w-4 shrink-0", isDark ? "text-slate-500" : "text-slate-300")} />
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className={cn("text-sm font-medium truncate", isDark ? "text-slate-200" : "text-slate-800")}>
                                            {c.concept}
                                        </p>
                                        <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>
                                            {c.subject} &middot; {c.question_count} card{c.question_count !== 1 ? 's' : ''} &middot; {c.total_misses} miss{c.total_misses !== 1 ? 'es' : ''}
                                        </p>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
                    <div className={cn("flex-1 text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                        {selected.size > 0 ? `${selected.size} concept${selected.size !== 1 ? 's' : ''} selected (${totalCards} card${totalCards !== 1 ? 's' : ''})` : 'No concepts selected'}
                    </div>
                    <Button
                        onClick={handleExport}
                        disabled={selected.size === 0 || exporting}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="anki-export-btn"
                    >
                        {exporting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <Download className="h-4 w-4 mr-2" />
                                Export .apkg
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
